import type { ComponentProps } from 'react';
import React from 'react';
import { View } from 'react-native';
import {
  DisplayText,
  TitleText,
  TYPE_CONFIG,
  useResponsive,
} from '@sweetgreen/garnish';

export const HomeScreenGreetings = React.memo(
  (props: HomeScreenGreetingsProps) => {
    const { greeting, heroHeadline, style, ...rest } = props;

    return (
      <View style={style} {...rest}>
        <GreetingText testID="homepage.welcome-text">{greeting}</GreetingText>

        <HeroHeadlineText>{heroHeadline}</HeroHeadlineText>
      </View>
    );
  },
);

// ─── Text Components ──────────────────────────────────────────────

const GreetingText = React.memo((props: GreetingTextProps) => {
  const { children, style, ...rest } = props;

  const { fluid } = useResponsive();
  const fluidStyles = {
    fontSize: fluid(TYPE_CONFIG.DISPLAY['32'].fontSize ?? 32, 56),
    lineHeight: fluid(TYPE_CONFIG.DISPLAY['32'].lineHeight ?? 36, 56),
    letterSpacing: fluid(0, 1),
  };

  const styles = [fluidStyles, style];

  return (
    <DisplayText
      accessibilityRole="header"
      accessibilityLevel={1}
      style={styles}
      maxFontSizeMultiplier={1.1}
      {...rest}
    >
      {children}
    </DisplayText>
  );
});

const HeroHeadlineText = React.memo((props: HeroHeadlineProps) => {
  const { children, style, ...rest } = props;
  const { min, max } = heroHeadlineTextFluidStyles;

  const { fluid } = useResponsive();

  const fluidStyles = {
    fontSize: fluid(min.fontSize, max.fontSize),
    lineHeight: fluid(min.lineHeight, max.lineHeight),
    letterSpacing: fluid(min.letterSpacing, max.letterSpacing),
  };

  const styles = [fluidStyles, style];

  return (
    <TitleText style={styles} maxFontSizeMultiplier={1.1} {...rest}>
      {children}
    </TitleText>
  );
});

// ─── Styles ───────────────────────────────────────────────────────

const heroHeadlineTextFluidStyles = {
  // NOTE: Because `TYPE CONFIG` uses the `TextStyle` type, which can allows
  //       having `undefined` values, fallback `0` values are added to avoid TS from
  //       reporting a 'undefined' value problem.
  min: {
    fontSize: TYPE_CONFIG.TITLE['32'].fontSize ?? 0,
    lineHeight: TYPE_CONFIG.TITLE['32'].lineHeight ?? 0,
    letterSpacing: TYPE_CONFIG.TITLE['32'].letterSpacing ?? 0,
  },
  max: {
    fontSize: 56,
    lineHeight: 56,
    letterSpacing: -3,
  },
};

// ─── Types ────────────────────────────────────────────────────────

type GreetingTextProps = {
  readonly children: React.ReactText;
} & ComponentProps<typeof DisplayText>;

type HeroHeadlineProps = {
  readonly children: React.ReactText;
} & ComponentProps<typeof TitleText>;

type HomeScreenGreetingsProps = {
  readonly greeting: string;
  readonly heroHeadline: string;
} & ComponentProps<typeof View>;
